import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { ReservationConfig, ReservationConfigUpdateBody } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const CONFIG_PATH = '/api/restaurants/{restaurantId}/reservation-config';

export function useReservationConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  return useQuery({
    queryKey: ['reservation-config', restaurantId],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<ReservationConfig>(
        await openapiClient.GET(CONFIG_PATH, { params: { path: { restaurantId } } }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useUpdateReservationConfig() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const qc = useQueryClient();
  const key = ['reservation-config', restaurantId];
  return useMutation({
    mutationFn: async (body: ReservationConfigUpdateBody) => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<ReservationConfig>(
        await openapiClient.PUT(CONFIG_PATH, { params: { path: { restaurantId } }, body }),
      );
    },
    // Optimistically merge the patch into the cached config so inline controls
    // reflect the change INSTANTLY. A controlled <Toggle> is bound to this server
    // state - without this it snaps back to the old value while the round-trip is
    // in flight, reading as "the toggle won't flip". Rolled back on error.
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ReservationConfig>(key);
      if (previous) qc.setQueryData<ReservationConfig>(key, { ...previous, ...patch });
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) qc.setQueryData<ReservationConfig>(key, ctx.previous);
    },
    // Reconcile with the server's canonical row once settled (success or error).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}
